import React, { useState } from 'react';
import { Menu, X, Sun, Moon, LogOut, Shield } from 'lucide-react';
import { useTema, useTemaEnLaPagina } from '../ui/theme';

/**
 * Armazón de los paneles.
 *
 * Reemplaza las pestañas por una navegación lateral: con siete secciones, las
 * pestañas se apretaban unas contra otras y en el teléfono ni se leían. La
 * barra lateral aguanta crecer y deja el contenido respirar.
 */
export const Armazon = ({
  titulo,
  subtitulo,
  secciones,
  activa,
  onCambiar,
  onSalir,
  acciones,
  children,
}) => {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const { tema, alternar } = useTema();

  // El tema oscuro se aplica solo mientras se esta dentro del panel: fuera de
  // aqui no hay estilos oscuros y el texto quedaria invisible.
  useTemaEnLaPagina(tema);

  const ir = (id) => {
    onCambiar(id);
    setMenuAbierto(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Barra superior en móvil */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-4 h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <button
          type="button"
          onClick={() => setMenuAbierto((v) => !v)}
          className="p-2 -ml-2 text-slate-600 dark:text-slate-300"
          aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={menuAbierto}
        >
          {menuAbierto ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        <span className="font-heading font-semibold truncate">{titulo}</span>

        <button
          type="button"
          onClick={alternar}
          className="p-2 -mr-2 text-slate-600 dark:text-slate-300"
          aria-label={tema === 'oscuro' ? 'Usar tema claro' : 'Usar tema oscuro'}
        >
          {tema === 'oscuro' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>

      <div className="lg:flex">
        {/* Navegación lateral */}
        <aside
          className={`${
            menuAbierto ? 'block' : 'hidden'
          } lg:block lg:sticky lg:top-0 lg:h-screen w-full lg:w-64 flex-none bg-white dark:bg-slate-900 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-800`}
        >
          <div className="hidden lg:flex items-center gap-2.5 px-5 h-16 border-b border-slate-200 dark:border-slate-800">
            <span className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center flex-none">
              <Shield className="w-4 h-4 text-white" />
            </span>
            <span className="min-w-0">
              <span className="block font-heading font-semibold text-sm truncate">{titulo}</span>
              {subtitulo && (
                <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                  {subtitulo}
                </span>
              )}
            </span>
          </div>

          <nav className="p-3 space-y-0.5" aria-label="Secciones del panel">
            {secciones.map((s) => {
              const Icono = s.icono;
              const esActiva = activa === s.id;

              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => ir(s.id)}
                  aria-current={esActiva ? 'page' : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    esActiva
                      ? 'bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icono className="w-4 h-4 flex-none" />
                  <span className="flex-1 text-left truncate">{s.nombre}</span>
                  {s.insignia > 0 && (
                    <span className="flex-none min-w-5 h-5 px-1.5 rounded-full bg-teal-600 text-white text-xs flex items-center justify-center">
                      {s.insignia}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="p-3 mt-auto border-t border-slate-200 dark:border-slate-800 space-y-0.5">
            <button
              type="button"
              onClick={alternar}
              className="hidden lg:flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              {tema === 'oscuro' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {tema === 'oscuro' ? 'Tema claro' : 'Tema oscuro'}
            </button>

            <button
              type="button"
              onClick={onSalir}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        </aside>

        {/* Contenido */}
        <main className="flex-1 min-w-0">
          <div className="hidden lg:flex items-center justify-between gap-4 px-8 h-16 border-b border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 backdrop-blur sticky top-0 z-30">
            <h1 className="font-heading text-lg font-semibold">
              {secciones.find((s) => s.id === activa)?.nombre}
            </h1>
            {acciones}
          </div>

          <div className="p-4 md:p-8 max-w-6xl">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Armazon;
